import { CALC_SOURCES, COLUMN_CATALOG_MAP, ENTITY_SCOPABLE_GROUPINGS } from './constants'
import { applyDerivedColumns, isDerivedColumnType } from './derivedColumns'
import { applyComparisonColumns, isComparisonColumnType } from './comparisonColumns'
import { applyWindowColumns, applySiblingWindowColumns, isWindowColumnType } from './windowColumns'
import { expandColumnsWithDerivedDeps, resolveCalcAssignments } from './buildOrionRequest'

let rowIdCounter = 0

/**
 * Builds a reverse lookup: calcId -> [{ columnId, field, sourceName }]
 * so we can map each Orion response calculation back to the columns that need it.
 */
function buildCalcToColumnMap (calcAssignments, columnInstances) {
  const columnMap = new Map(columnInstances.map(c => [c.id, c]))
  const calcIdToColumns = new Map()

  for (const group of calcAssignments.values()) {
    const entries = []
    for (const colId of group.columnIds) {
      const col = columnMap.get(colId)
      if (!col) continue
      const catalog = COLUMN_CATALOG_MAP[col.columnType]
      if (!catalog) continue
      entries.push({ columnId: colId, field: catalog.field, sourceName: catalog.calcSource })
    }
    calcIdToColumns.set(group.calcId, entries)
  }

  return calcIdToColumns
}

/**
 * Extracts flat column values from the calculation results on a group node.
 * Uses the calc assignments to route each Orion response calc to the columns
 * that reference it, pulling the specific field each column needs.
 */
function extractColumns (calculations, calcIdToColumns, allowedColumnIds) {
  const columns = {}
  if (!calculations || !calcIdToColumns) return columns

  for (const calc of calculations) {
    if (!calc.id) continue
    const mappings = calcIdToColumns.get(calc.id)
    if (!mappings) continue

    for (const { columnId, field, sourceName } of mappings) {
      if (allowedColumnIds && !allowedColumnIds.has(columnId)) continue
      const source = CALC_SOURCES[sourceName]
      if (!source) continue
      const fieldDef = source.fields[field]
      if (!fieldDef) continue
      columns[columnId] = fieldDef.extract(calc)
    }
  }

  return columns
}

/**
 * Recursively transforms a calculated-group node into the row shape for the grid.
 * When hasMoreLevels is true, leaf groups are marked as expandable but not yet loaded.
 * Tracks nearest entity-scopable ancestor for hybrid lazy expand scoping.
 */
function transformGroupNode (group, calcIdToColumns, columnInstances, calcAssignments, globalDateConfig, materialColumnIds, depth = 0, hasMoreLevels = false, ancestorEntity = null, ancestorEntityIds = null, parentRow = null, totalRow = null) {
  const isScopable = ENTITY_SCOPABLE_GROUPINGS.has(group.grouping)
  const nextAncestorEntity = isScopable ? group.grouping : ancestorEntity
  const nextAncestorEntityIds = isScopable ? [group.groupEntityId] : ancestorEntityIds

  const leafCalcs = (group.calculations || []).filter(c => c.$type !== 'calculated-list')
  const nestedList = (group.calculations || []).find(c => c.$type === 'calculated-list')

  const row = {
    _rowId: `row_${rowIdCounter++}`,
    name: group.name || group.grouping || 'Unknown',
    grouping: group.grouping,
    groupId: group.groupId,
    groupEntityId: group.groupEntityId,
    depth,
    _ancestorEntity: ancestorEntity,
    _ancestorEntityIds: ancestorEntityIds,
    ...extractColumns(leafCalcs, calcIdToColumns, materialColumnIds),
    _childrenLoaded: true,
    data: undefined
  }
  applyDerivedColumns(row, columnInstances, COLUMN_CATALOG_MAP, calcAssignments, leafCalcs, globalDateConfig)
  applyComparisonColumns(row, columnInstances, COLUMN_CATALOG_MAP)
  applyWindowColumns(row, parentRow, totalRow, columnInstances, COLUMN_CATALOG_MAP)

  const children = nestedList
    ? (nestedList.calculations || [])
      .filter(c => c.$type === 'calculated-group')
      .map(child => transformGroupNode(child, calcIdToColumns, columnInstances, calcAssignments, globalDateConfig, materialColumnIds, depth + 1, hasMoreLevels, nextAncestorEntity, nextAncestorEntityIds, row, totalRow))
    : []

  if (children.length > 0) {
    applySiblingWindowColumns(children, columnInstances, COLUMN_CATALOG_MAP)
  }

  const isLeafGrouping = children.length === 0
  const needsLazyChildren = isLeafGrouping && hasMoreLevels

  row._childrenLoaded = !needsLazyChildren
  row.data = needsLazyChildren ? [{ _placeholder: true, name: '' }] : (children.length > 0 ? children : undefined)

  return row
}

/**
 * Transforms the gateway response into a tree of rows for the grid.
 * The gateway returns { data } where data is the raw Orion response with a calculations array.
 *
 * @param {object} response - The gateway response { data }
 * @param {{ columnInstances?: Array, globalDateConfig?: object, hasMoreLevels?: boolean, entity?: string, entityIds?: number[] }} options
 */
export function transformResponse (response, options = {}) {
  if (!response) return { rows: [], totalRow: null, metadata: null }

  const orionData = response.data
  const hasMoreLevels = options.hasMoreLevels ?? false
  const columnInstances = options.columnInstances || []
  const globalDateConfig = options.globalDateConfig || {}
  const rootEntity = options.entity ?? null
  const rootEntityIds = options.entityIds ?? null

  if (!orionData?.calculations) {
    return { rows: [], totalRow: null, metadata: null }
  }

  const expandedColumns = expandColumnsWithDerivedDeps(columnInstances, globalDateConfig)
  const calcAssignments = resolveCalcAssignments(expandedColumns, globalDateConfig)
  const calcIdToColumns = buildCalcToColumnMap(calcAssignments, expandedColumns)
  const materialColumnIds = new Set(
    columnInstances
      .filter(c => !String(c.id).startsWith('__req_') && !isDerivedColumnType(c.columnType) && !isWindowColumnType(c.columnType) && !isComparisonColumnType(c.columnType))
      .map(c => c.id)
  )

  const topList = orionData.calculations.find(c => c.$type === 'calculated-list')
  if (!topList) {
    // TODO: zero-grouping responses have no calculated-list but may still contain
    // top-level leaf calcs that should populate totalRow. Not reachable today
    // (UI enforces >= 1 grouping), but worth handling if that constraint relaxes.
    return { rows: [], totalRow: null, metadata: null }
  }

  const topLeafCalcs = orionData.calculations.filter(
    c => c.$type !== 'calculated-list' && c.$type !== 'grouping'
  )

  let totalRow = null
  if (topLeafCalcs.length > 0) {
    totalRow = {
      _rowId: 'total',
      _isTotal: true,
      name: 'Total',
      depth: 0,
      ...extractColumns(topLeafCalcs, calcIdToColumns, materialColumnIds),
      _childrenLoaded: true,
      data: undefined
    }
    applyDerivedColumns(totalRow, columnInstances, COLUMN_CATALOG_MAP, calcAssignments, topLeafCalcs, globalDateConfig)
  }

  const groups = (topList.calculations || []).filter(c => c.$type === 'calculated-group')
  const rows = groups.map(group => transformGroupNode(group, calcIdToColumns, columnInstances, calcAssignments, globalDateConfig, materialColumnIds, 0, hasMoreLevels, rootEntity, rootEntityIds, totalRow, totalRow))

  applySiblingWindowColumns(rows, columnInstances, COLUMN_CATALOG_MAP)

  return { rows, totalRow, metadata: null }
}

/**
 * Transforms a child response and returns just the child rows.
 * Used when lazily expanding a parent row.
 *
 * @param {object} response
 * @param {object} options - Same as transformResponse, plus `parentRow` and `totalRow`
 *   for window column computation during lazy expand.
 */
export function transformChildResponse (response, options = {}) {
  const { rows } = transformResponse(response, options)

  const { parentRow, totalRow, columnInstances = [] } = options
  if (parentRow || totalRow) {
    const applyWindowRecursive = (rowList, parent) => {
      for (const row of rowList) {
        applyWindowColumns(row, parent, totalRow, columnInstances, COLUMN_CATALOG_MAP)
        if (row.data && Array.isArray(row.data) && !row.data[0]?._placeholder) {
          applyWindowRecursive(row.data, row)
        }
      }
      applySiblingWindowColumns(rowList, columnInstances, COLUMN_CATALOG_MAP)
    }
    applyWindowRecursive(rows, parentRow)
  }

  return rows
}
